// import { watchEffect } from 'vue'
import { enableTracking, resetTracking, track, trigger, watch } from '@vue/reactivity';

export default class ModelEditor {
  
  static workingObj;
  static origObj;
  static changedAttributes;
  
  constructor(obj, entity, delegate, options={}) {
    this.changedAttributes = new Set();
    this.entity = entity;
    this.origObj = obj;
    this.delegate = delegate;
    this.rootEditor = options.rootEditor || this;
    this.parentEditor = options.parentEditor;
    this.parentRelationName = options.parentRelationName;
    this.parentRelationship = options.parentRelationship;
    this.ancestorEntities = options.ancestorEntities || (entity ? [entity] : []);
    this.relationshipStates = new Map();
    this.nestedEditors = new WeakMap();
    this._syncing = false; // true when handling externally pushed updates
    this._errors = {}; // attribute -> error

    this.setWorkingObj(obj);
    
    // Hook up notifications when the origObj keys change
    //
    Object.keys(obj).forEach(k => this.observe(k));
  }
  
  setWorkingObj(obj) {
    this.workingObj = Object.assign(Object.create(this.entity?.proto || null), obj);
    this.normalizeWorkingRelationshipProperties();
    this.relationshipStates?.clear();
    if (this.rootEditor === this) {
      this.nestedEditors = new WeakMap();
    }
  }

  normalizeWorkingRelationshipProperties() {
    if (!this.entity?.relationships) return;

    Object.keys(this.entity.relationships).forEach(relName => {
      if (!Object.hasOwn(this.workingObj, relName)) return;

      const relationship = this.entity.relationships[relName];
      const backingAttr = this.getRelationshipBackingIdAttr(relName, relationship);
      if (!Object.hasOwn(this.workingObj, backingAttr)) {
        this.workingObj[backingAttr] = this.normalizeRelationshipValue(relationship, this.workingObj[relName]);
      }
      delete this.workingObj[relName];
    });
  }

  getRelationship(attr) {
    return this.entity?.relationships?.[attr];
  }

  // Map backing attributes like `raceId` or `crossingsIds` back to the
  // relationship name (`race`, `crossings`) when they belong to this entity.
  getRelationshipNameForBackingAttr(attr) {
    if (!this.entity?.relationships || typeof attr !== 'string')
      return undefined;

    if (attr.endsWith('Ids')) {
      const relName = attr.slice(0, -3);
      if (this.entity.relationships[relName]) return relName;
    }

    if (attr.endsWith('Id')) {
      const relName = attr.slice(0, -2);
      if (this.entity.relationships[relName]) return relName;
    }

    return undefined;
  }

  // Resolve the underlying ID attribute for a relationship, sorting
  // out the one-to-one `fooId` vs to-many `fooIds` naming for callers.
  // 'foo' -> 'fooId'
  getRelationshipBackingIdAttr(relName, relationship) {
    return relName + (relationship.repeat ? 'Ids' : 'Id');
  }

  // Convert relationship assignments into the stored ID form:
  // objects become their IDs, and to-many assignments become ID arrays.
  normalizeRelationshipValue(relationship, value) {
    if (relationship.repeat) {
      if (!Array.isArray(value)) return [];
      return value.map(v => (typeof v === 'object' && v !== null) ? v[relationship.entity.idAttr] : v);
    }

    return (typeof value === 'object' && value !== null) ?
      value[relationship.entity.idAttr] :
      value;
  }

  entityId(entity, obj) {
    return obj?.[entity?.idAttr || 'id'];
  }

  isEphemeralId(id) {
    return typeof id === 'string' && id.startsWith('NEW-');
  }

  arrayValuesEqual(a, b) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((value, index) => value === b[index]);
  }

  sameId(a, b) {
    return String(a) === String(b);
  }

  isRelationshipChanged(relName) {
    const relationship = this.getRelationship(relName);
    if (!relationship) return false;

    const backingAttr = this.getRelationshipBackingIdAttr(relName, relationship);
    return this.changedAttributes.has(relName) || this.changedAttributes.has(backingAttr);
  }

  markRelationshipChanged(relName) {
    if (!relName || this._syncing) return;

    this.changedAttributes.add(relName);

    if (this.parentEditor) {
      this.parentEditor.markRelationshipChanged(this.parentRelationName);
    }
  }

  markNestedChanged(attr, value) {
    if (this.parentEditor && !this._syncing) {
      this.parentEditor.markRelationshipChanged(this.parentRelationName);
      if (this.delegate?.modelDidChange) {
        this.delegate.modelDidChange(this.rootEditor, this.parentRelationName, this.parentEditor.workingObj[this.parentRelationName]);
      }
    }
  }

  proxyForNestedObject(obj, entity, relName, relationship) {
    if (!obj || typeof obj !== 'object') return obj;

    const existing = this.rootEditor.nestedEditors.get(obj);
    if (existing) return existing.proxy;

    const nextAncestors = [...this.ancestorEntities, entity].filter(Boolean);
    const handler = new ModelEditor(obj, entity, this.delegate, {
      rootEditor: this.rootEditor,
      parentEditor: this,
      parentRelationName: relName,
      parentRelationship: relationship,
      ancestorEntities: nextAncestors,
    });
    const proxy = new Proxy(obj, handler);
    handler.proxy = proxy;
    this.rootEditor.nestedEditors.set(obj, { handler, proxy });
    return proxy;
  }

  nestedHandlerForObject(obj) {
    return this.rootEditor.nestedEditors.get(obj)?.handler;
  }

  canWrapNestedRelationship(relationship) {
    if (!relationship?.entity) return false;
    return !this.ancestorEntities.includes(relationship.entity);
  }

  inverseRelationshipName(relationship) {
    if (relationship.inverse !== undefined) return relationship.inverse;

    const owner = relationship.owner;
    const targetRelationships = relationship.entity?.relationships || {};
    const matches = Object.keys(targetRelationships).filter(rel => {
      return targetRelationships[rel].entity === owner;
    });

    relationship.inverse = matches.length === 1 ? matches[0] : null;
    return relationship.inverse;
  }

  isBelongsToRelationship(relationship) {
    if (!relationship || relationship.repeat) return false;

    const inverseName = this.inverseRelationshipName(relationship);
    if (!inverseName) return false;

    return relationship.entity?.relationships?.[inverseName]?.repeat === true;
  }

  relationshipIdsForValue(relationship, value) {
    const normalized = this.normalizeRelationshipValue(relationship, value);
    if (relationship.repeat) return Array.isArray(normalized) ? normalized : [];
    return normalized == null ? [] : [normalized];
  }

  relationshipIdsFromBacking(relName, relationship) {
    const backingAttr = this.getRelationshipBackingIdAttr(relName, relationship);
    const value = this.workingObj[backingAttr];

    if (relationship.repeat) {
      return Array.isArray(value) ? value.filter(v => v != null) : [];
    }

    return value == null ? [] : [value];
  }

  relationshipObjectForId(relationship, id) {
    if (id == null) return undefined;
    const bucket = relationship.entity?.store?.[relationship.entity.key];
    return bucket?.[id];
  }

  materializeRelationshipObjects(relName, relationship) {
    return this.relationshipIdsFromBacking(relName, relationship)
      .map(id => this.relationshipObjectForId(relationship, id))
      .filter(obj => obj && typeof obj === 'object');
  }

  relationshipObjectsForValue(relationship, value) {
    if (relationship.repeat) {
      if (!Array.isArray(value)) return [];
      return value.filter(v => v && typeof v === 'object');
    }

    return value && typeof value === 'object' ? [value] : [];
  }

  getRelationshipState(relName, relationship) {
    let state = this.relationshipStates.get(relName);
    if (state) return state;

    let originalIds = this.relationshipIdsFromBacking(relName, relationship);
    if (!originalIds.length) {
      originalIds = this.relationshipIdsForValue(relationship, this.workingObj[relName]);
    }

    state = {
      relName,
      relationship,
      originalIds,
      removed: new Map(),
      target: relationship.repeat ? [] : undefined,
      proxy: undefined,
    };
    this.relationshipStates.set(relName, state);
    return state;
  }

  updateRemovedRelationshipIds(state, nextIds, previousIds) {
    previousIds.forEach(id => {
      if (id == null || this.isEphemeralId(id)) return;
      if (!nextIds.some(nextId => this.sameId(nextId, id))) {
        state.removed.set(String(id), id);
      }
    });

    nextIds.forEach(id => {
      if (id == null) return;
      state.removed.delete(String(id));
    });
  }

  syncRelationshipProxyTarget(state) {
    const nextObjects = this.materializeRelationshipObjects(state.relName, state.relationship);
    state.target.splice(0, state.target.length, ...nextObjects);
    return state.target;
  }

  syncRelationshipArrayState(state, nextValue, shouldMarkChanged=true, previousIds=this.relationshipIdsFromBacking(state.relName, state.relationship)) {
    const relationship = state.relationship;
    const nextObjects = this.relationshipObjectsForValue(relationship, nextValue);
    const backingAttr = this.getRelationshipBackingIdAttr(state.relName, relationship);
    const nextIds = this.normalizeRelationshipValue(relationship, nextObjects);

    this.updateRemovedRelationshipIds(state, nextIds, previousIds);
    this.workingObj[backingAttr] = nextIds;
    this.syncRelationshipProxyTarget(state);

    if (shouldMarkChanged) {
      this.markRelationshipChanged(state.relName);
      trigger(this.workingObj, 'set', backingAttr, this.workingObj[backingAttr]);
      trigger(this, 'set', state.relName);
    }
  }

  relationshipArrayProxy(relName, relationship) {
    const state = this.getRelationshipState(relName, relationship);
    if (state.proxy) return state.proxy;

    const editor = this;
    state.proxy = new Proxy(state.target, {
      get(target, attr, receiver) {
        if (attr === '__v_isReactive') return true;
        editor.syncRelationshipProxyTarget(state);
        if (attr === '_rawRelationshipItems') return target;

        const value = Reflect.get(target, attr, receiver);
        if (typeof value === 'function' && ['push', 'pop', 'shift', 'unshift', 'splice', 'sort', 'reverse'].includes(attr)) {
          return function(...args) {
            editor.syncRelationshipProxyTarget(state);
            const previousIds = editor.relationshipIdsFromBacking(state.relName, relationship);
            const result = Array.prototype[attr].apply(target, args);
            editor.syncRelationshipArrayState(state, target, true, previousIds);
            return result;
          };
        }

        const isArrayIndex = typeof attr === 'string' && String(Number(attr)) === attr;
        if (isArrayIndex && target[attr]) {
          return editor.proxyForNestedObject(target[attr], relationship.entity, relName, relationship);
        }

        return value;
      },
      set(target, attr, value) {
        editor.syncRelationshipProxyTarget(state);
        const previousIds = editor.relationshipIdsFromBacking(state.relName, relationship);
        const ok = Reflect.set(target, attr, value);
        editor.syncRelationshipArrayState(state, target.filter(Boolean), true, previousIds);
        return ok;
      },
      deleteProperty(target, attr) {
        editor.syncRelationshipProxyTarget(state);
        const previousIds = editor.relationshipIdsFromBacking(state.relName, relationship);
        const ok = Reflect.deleteProperty(target, attr);
        editor.syncRelationshipArrayState(state, target.filter(Boolean), true, previousIds);
        return ok;
      },
    });

    return state.proxy;
  }

  relationshipValue(relName, relationship) {
    track(this, 'get', relName);

    if (!this.canWrapNestedRelationship(relationship)) {
      return this.workingObj[relName];
    }

    if (relationship.repeat) {
      return this.relationshipArrayProxy(relName, relationship);
    }

    const value = this.workingObj[relName];
    return this.proxyForNestedObject(value, relationship.entity, relName, relationship);
  }

  payloadForChangedObject(editor, includeAll=false, visited=new WeakSet()) {
    const obj = editor.workingObj;
    if (!obj || typeof obj !== 'object' || visited.has(obj)) return undefined;
    visited.add(obj);

    const payload = {};
    const idAttr = editor.entity?.idAttr || 'id';
    const id = obj[idAttr];
    if (id != null) payload[idAttr] = id;
    if (obj.ephemeralKey) payload.ephemeralKey = obj.ephemeralKey;

    if (includeAll) {
      Object.keys(obj).forEach(attr => {
        if (editor.getRelationshipNameForBackingAttr(attr)) return;
        if (editor.getRelationship(attr)) return;
        payload[attr] = obj[attr];
      });
    }
    else {
      editor.changedAttributes.forEach(attr => {
        if (editor.getRelationshipNameForBackingAttr(attr)) return;
        if (editor.getRelationship(attr)) return;
        payload[attr] = obj[attr];
      });
    }

    if (editor.entity?.relationships) {
      Object.keys(editor.entity.relationships).forEach(relName => {
        const relationship = editor.entity.relationships[relName];
        const backingAttr = editor.getRelationshipBackingIdAttr(relName, relationship);
        const shouldIncludeRelationship = editor.isRelationshipChanged(relName) ||
          (includeAll &&
            !relationship.repeat &&
            editor.canWrapNestedRelationship?.(relationship) &&
            obj[backingAttr] != null);
        if (!shouldIncludeRelationship) return;

        if (relationship.repeat) {
          const state = editor.getRelationshipState(relName, relationship);
          const currentIds = editor.relationshipIdsFromBacking(relName, relationship);
          const membershipChanged = !editor.arrayValuesEqual(state.originalIds, currentIds) || state.removed.size > 0;
          const originalIds = new Set(state.originalIds.map(id => String(id)));
          const children = currentIds.map(childId => {
            const child = editor.relationshipObjectForId(relationship, childId);
            const childEditor = child ? editor.nestedHandlerForObject(child) : undefined;

            if (childEditor) {
              const childPayload = editor.payloadForChangedObject(childEditor, false, visited);
              if (Object.keys(childPayload || {}).length > 1) return childPayload;
              if (!membershipChanged) return undefined;
              if (editor.isEphemeralId(childId)) {
                return editor.payloadForChangedObject(childEditor, true, visited);
              }
              if (childId != null && !originalIds.has(String(childId))) {
                return { [relationship.entity.idAttr || 'id']: childId };
              }
              return undefined;
            }

            if (!membershipChanged) return undefined;

            if (editor.isEphemeralId(childId) && child) {
              return editor.payloadForChangedObject({
                workingObj: child,
                entity: relationship.entity,
                changedAttributes: new Set(),
                getRelationshipNameForBackingAttr: editor.getRelationshipNameForBackingAttr.bind({ entity: relationship.entity }),
                getRelationship: attr => relationship.entity?.relationships?.[attr],
                getRelationshipBackingIdAttr: editor.getRelationshipBackingIdAttr,
                canWrapNestedRelationship: editor.canWrapNestedRelationship.bind(editor),
                isBelongsToRelationship: editor.isBelongsToRelationship.bind(editor),
                entityId: editor.entityId.bind(editor),
                isEphemeralId: editor.isEphemeralId.bind(editor),
                nestedHandlerForObject: editor.nestedHandlerForObject.bind(editor),
                isRelationshipChanged: () => false,
              }, true, visited);
            }

            if (childId == null || originalIds.has(String(childId))) return undefined;
            return { [relationship.entity.idAttr || 'id']: childId };
          }).filter(Boolean);

          state.removed.forEach((removedId) => {
            if (removedId == null) return;
            children.push({ [relationship.entity.idAttr || 'id']: removedId, _destroy: true });
          });

          payload[relName] = children;
          return;
        }

        const child = obj[relName];
        if (editor.isBelongsToRelationship?.(relationship)) {
          const relationshipMembershipChanged = includeAll || editor.changedAttributes?.has(backingAttr);
          const childId = child ? editor.entityId(relationship.entity, child) : undefined;
          const childEditor = editor.nestedHandlerForObject?.(child);

          if (!relationshipMembershipChanged && childEditor) {
            const childPayload = editor.payloadForChangedObject(childEditor, false, visited);
            if (Object.keys(childPayload || {}).length > 1) {
              payload[relName] = childPayload;
              return;
            }
          }

          if (relationshipMembershipChanged) {
            payload[backingAttr] = obj[backingAttr] ?? childId ?? null;
          }
          return;
        }

        if (!child) {
          payload[relName] = null;
          return;
        }

        const childIdAttr = relationship.entity.idAttr || 'id';
        const childId = editor.entityId(relationship.entity, child);
        const childEditor = editor.nestedHandlerForObject(child);

        if (childEditor) {
          const childPayload = editor.payloadForChangedObject(childEditor, false, visited);
          if (Object.keys(childPayload || {}).length > 1) {
            payload[relName] = childPayload;
            return;
          }
        }

        if (childId != null && !editor.isEphemeralId(childId)) {
          payload[relName] = { [childIdAttr]: childId };
          return;
        }

        payload[relName] = this.payloadForChangedObject({
          workingObj: child,
          entity: relationship.entity,
          changedAttributes: new Set(),
          getRelationshipNameForBackingAttr: editor.getRelationshipNameForBackingAttr.bind({ entity: relationship.entity }),
          getRelationship: attr => relationship.entity?.relationships?.[attr],
          getRelationshipBackingIdAttr: editor.getRelationshipBackingIdAttr,
          canWrapNestedRelationship: editor.canWrapNestedRelationship.bind(editor),
          isBelongsToRelationship: editor.isBelongsToRelationship.bind(editor),
          entityId: editor.entityId.bind(editor),
          isEphemeralId: editor.isEphemeralId.bind(editor),
          nestedHandlerForObject: editor.nestedHandlerForObject.bind(editor),
          isRelationshipChanged: () => false,
        }, true, visited);
      });
    }

    return payload;
  }

  buildPayload() {
    return this.payloadForChangedObject(this);
  }
  
  observe(key) {
    const obj = this.origObj;
    watch(() => obj[key], (v, oldVal) => {
      if (this.delegate?.modelSourceDidChange(obj, key, v)) {
        // Delegate returned true; they want us to update the
        // model object too. We *will not* mark it as a changed
        // attribute, since this change came from outside the
        // model change form (probably from a websocket).
        // console.debug(`model-editor: lost sync on ${key}`, v) ;
        this._syncing = true;
        this.set(this,key,v);
        this._syncing = false;
      }
    });
  }
  
    update(newObj=null) {
      const sourceObj = newObj || this.origObj;
      const keys = Object.keys(sourceObj);
      let k;
      for (let i = keys.length - 1; i >= 0; i--) {
        k = keys[i]
        // console.debug(`model-editor: update ${k}`);
        if (!Object.hasOwn(this.workingObj, k)) {
          this.observe(k);
        }
        this._syncing = true;
        this.set(this, k, sourceObj[k]);
        this._syncing = false;
      }
    }

  get(target, attr, receiver) {
    if (attr === '_changedAttributes') return this.changedAttributes;
    if (attr === '_commitValues') return this.commitValues.bind(this); // otherwise `this` will be the proxy
    if (attr === '_update') return this.update.bind(this); // otherwise `this` will be the proxy
    if (attr === '_payload') return this.buildPayload.bind(this);
    
    if (attr === '__v_isReactive')
      return true;

    const relationship = this.getRelationship(attr);
    if (relationship) {
      return this.relationshipValue(attr, relationship);
    }

    track(this.workingObj, 'get', attr);          // depend on workingObj

    return this.workingObj[attr];
  }
  
  set(target, attr, value) {
    // If they're setting e.g. `raceId`, but templates reference `race`,
    // Vue's reactivity system will miss that one depends on the other.
    // We cross-trigger these automatically.
    //
    let actualAttr = attr;
    let actualValue = value;
    let relationName = this.getRelationshipNameForBackingAttr(attr);
    let relationship = relationName ? this.getRelationship(relationName) : this.getRelationship(attr);
    let relationshipState;
    let previousRelationshipIds;
    let settingRelationshipProperty = false;

    if (relationship && !relationName) {
      relationName = attr;
      actualAttr = this.getRelationshipBackingIdAttr(relationName, relationship);
      actualValue = this.normalizeRelationshipValue(relationship, value);
      settingRelationshipProperty = true;
    }

    if (relationship?.repeat && relationName) {
      relationshipState = this.getRelationshipState(relationName, relationship);
      previousRelationshipIds = this.relationshipIdsFromBacking(relationName, relationship);
    }

    // console.debug(`set ${actualAttr}=${actualValue}`)
    
    if (!this._syncing) {
      this.changedAttributes.add(actualAttr);
      if (relationName) this.changedAttributes.add(relationName);
    }
    
    // catch newly assigned attributes
    if (!Object.hasOwn(this.workingObj, actualAttr)) {
      this.observe(actualAttr);
    }
    
    this.workingObj[actualAttr] = actualValue;

    if (relationship && relationName) {
      if (relationship.repeat) {
        if (settingRelationshipProperty) {
          this.syncRelationshipArrayState(relationshipState, value, false, previousRelationshipIds);
        }
        else {
          const nextIds = this.relationshipIdsFromBacking(relationName, relationship);
          this.updateRemovedRelationshipIds(relationshipState, nextIds, previousRelationshipIds);
          this.syncRelationshipProxyTarget(relationshipState);
        }
      }
      else {
        this.markRelationshipChanged(relationName);
      }
    }
    
    trigger(this.workingObj, 'set', actualAttr, actualValue);  // fire watchers on workingObj

    if (relationName) {
      trigger(this, 'set', relationName); // fire watchers on relationship lookups
    }

    this.markNestedChanged(actualAttr, actualValue);

    if (this.delegate?.modelDidChange && !this._syncing)
      this.delegate.modelDidChange(this, actualAttr, actualValue);
        
    return true;
  }
  
  async commitValues() {
    console.debug("Committing values for object", this.origObj);
    this.changedAttributes.forEach(attr => {
      this.origObj[attr] = this.workingObj[attr];
    });

    this.changedAttributes.clear();
    this.relationshipStates.clear();
  }
}

// May be overwritten by a subclass
//
export function createModelProxy(obj, delegate) {
  let handler = new ModelEditor(obj, undefined, delegate);
  let proxy = new Proxy(obj, handler);
  
  return proxy;
}
